import { WrapperGS } from '../core/WrapperGS';
import { RuntimeEnvironmentGSEnum, RuntimeGS } from '../core/app/RuntimeGS';

interface FocusPositionGS {
    readonly element: HTMLElement;
    readonly groupName: string;
    readonly position: number;
}

const focusAttribute = 'gs-focus';
const listeningDocuments = new WeakSet<Document>();

function enableFocusNavigation(wrapper: WrapperGS): void {
    const scope = RuntimeGS.current;
    if (scope?.environment === RuntimeEnvironmentGSEnum.Server) return;

    if (!parseFocusPosition(wrapper.el)) {
        console.warn(`Invalid ${focusAttribute} value '${wrapper.el.getAttribute(focusAttribute) ?? ''}'. Expected 'group:position'.`);
        return;
    }

    const document = wrapper.el.ownerDocument;
    if (listeningDocuments.has(document)) return;
    document.addEventListener('keydown', handleFocusKeyDown);
    listeningDocuments.add(document);
}

function handleFocusKeyDown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.defaultPrevented || keyboardEvent.key !== 'Enter'
        || keyboardEvent.repeat || keyboardEvent.isComposing) return;

    const currentElement = keyboardEvent.target as HTMLElement | null;
    if (!currentElement?.hasAttribute?.(focusAttribute)) return;
    if (currentElement.tagName === 'TEXTAREA' && !keyboardEvent.ctrlKey) return;

    const current = parseFocusPosition(currentElement);
    const document = keyboardEvent.currentTarget as Document | null;
    if (!current || !document) return;

    const positions = collectGroupPositions(document, current.groupName);
    if (!positions) return;

    const next = positions.find(value => value.position > current.position);
    keyboardEvent.preventDefault();
    if (!next) {
        currentElement.blur();
        return;
    }

    activateElement(next.element);
}

function collectGroupPositions(
    document: Document,
    groupName: string
): FocusPositionGS[] | undefined {
    const positions: FocusPositionGS[] = [];
    const positionElements = new Map<number, HTMLElement>();
    for (const element of Array.from(document.querySelectorAll(`[${focusAttribute}]`))) {
        const value = parseFocusPosition(element as HTMLElement);
        if (!value || value.groupName !== groupName) continue;

        if (positionElements.has(value.position)) {
            console.warn(`Duplicate ${focusAttribute} position '${groupName}:${value.position}'.`);
            return undefined;
        }
        positionElements.set(value.position, value.element);
        positions.push(value);
    }
    positions.sort((left, right) => left.position - right.position);
    return positions;
}

function parseFocusPosition(element: HTMLElement): FocusPositionGS | undefined {
    const raw = element.getAttribute(focusAttribute)?.trim();
    if (!raw) return undefined;
    const separator = raw.lastIndexOf(':');
    const groupName = (separator < 0 ? raw : raw.slice(0, separator)).trim();
    const positionText = (separator < 0 ? '0' : raw.slice(separator + 1)).trim();
    if (!groupName || !/^\d+$/.test(positionText)) return undefined;
    const position = Number(positionText);
    if (!Number.isSafeInteger(position)) return undefined;
    return { element, groupName, position };
}

function activateElement(element: HTMLElement): void {
    if (element.getAttribute('type')?.toLowerCase() === 'submit') {
        element.click();
        return;
    }
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        element.focus();
        return;
    }
    element.click();
}

/** Programmatic convenience facade over the stateless gs-focus document behavior. */
export class FocusGroup {
    public static readonly TagGSA = 'focus';
    public readonly groupName: string;

    public constructor(wrapper: WrapperGS, groupName = 'noname', position = 0) {
        this.groupName = groupName.trim();
        this.add(wrapper, position);
    }

    public static AddItem(wrapper: WrapperGS): void {
        enableFocusNavigation(wrapper);
    }

    public add(wrapper: WrapperGS, position: number): boolean {
        if (!this.groupName || !Number.isSafeInteger(position) || position < 0) {
            console.warn(`Invalid ${focusAttribute} position '${this.groupName}:${position}'.`);
            return false;
        }
        wrapper.setGSA(FocusGroup.TagGSA, `${this.groupName}:${position}`);
        enableFocusNavigation(wrapper);
        return true;
    }

    public remove(wrapper: WrapperGS): void {
        const value = parseFocusPosition(wrapper.el);
        if (value?.groupName === this.groupName) wrapper.removeGSA(FocusGroup.TagGSA);
    }

    public clear(): void {
        const document = RuntimeGS.current?.document ?? RuntimeGS.window?.document;
        if (!document) return;
        for (const element of Array.from(document.querySelectorAll(`[${focusAttribute}]`))) {
            const htmlElement = element as HTMLElement;
            if (parseFocusPosition(htmlElement)?.groupName === this.groupName)
                htmlElement.removeAttribute(focusAttribute);
        }
    }
}

WrapperGS.ImplementWrapperByGSA(FocusGroup.TagGSA, FocusGroup.AddItem);
